import React, { useState, useEffect } from 'react';
import { Box, VStack, HStack, Switch, Divider } from 'native-base';
import AmountDropdown from './dropdowns/Amount';
import PaymentDropdown from './dropdowns/Payment';
import {
    GiftCard,
    GiftCardPayment,
    observer,
    i18nFn,
    useActivateGiftCard,
    useReloadGiftCard,
    useSpringBigStore,
    useCreateGiftCardWithPoints,
    getMenuItems, useTheme,
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SpIcon } from '~/shared/Icon';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Pressable from '~/shared/Pressable';
import { Button, CheckBox, } from '~/shared/Theme';

const AddFunds = ({
    manageGiftCard,
    t,
    isPurchaseWithPoints,
    setGiftCardValue,
    setIsCompletedModalOpen,
    isConfirmState,
    setIsConfirmState,
}: {
    manageGiftCard: GiftCard | null,
    t: i18nFn,
    successToast: (title: string, description?: string) => void,
    isPurchaseWithPoints: boolean;
    setGiftCardValue: (value: number) => void,
    setIsCompletedModalOpen: (value: boolean) => void,
    isConfirmState: boolean
    setIsConfirmState: (value: boolean) => void
}) => {

    const {
        primary,
        radius_sm,
        highlight_text,
    } = useTheme();

    const {
        member,
        merchant,
        giftCards,
        giftCardPresetAmounts,
    } = useSpringBigStore();

    const maxAmount = Math.min(
        Number(member?.remaining_dollar_amount_for_current_period) / 100,
        Number(merchant?.gc_dollar_amount_one_time)
    );

    const minAmount = Math.min(merchant.gc_min_price, 20);

    const [isFundWithPoints, setIsFundWithPoints] = useState(isPurchaseWithPoints);
    // const [errorCVV, setErrorCVV] = useState<string>('');
    const [convertPoints, setConvertPoints] = useState(0);
    // const [pointsAmount, setPointsAmount] = useState(0);
    const [amount, setAmount] = useState<number>(0); // remove ability to purchase GC altogether
    // const [amount, setAmount] = useState<number>(merchant.gift_card_restrictions ? maxAmount : 20.00);
    const [total, setTotal] = useState('$0.00');
    const [belowAmount, setbelowAmount] = useState<number>(
        parseFloat(manageGiftCard?.auto_reload_amount?.split('$')[0] || '20')
    );
    const [paymentMethod, setPaymentMethod] = useState<GiftCardPayment | null>(null);
    const [submitError, setSubmitError] = useState('');

    const {
        mutate: requestActivateGiftCard,
        error: requestActivateCardError,
        isLoading: activateGiftCardLoading
    } = useActivateGiftCard();

    const {
        mutate: reloadGiftCard,
        error: reloadGiftCardError,
        isLoading: reloadGiftCardLoading
    } = useReloadGiftCard();

    const {
        mutate: createWithPoints,
        error: createWithPointsError,
        isLoading: createWithPointsLoading,
    } = useCreateGiftCardWithPoints();


    const [isPaymentDisclaimer, setIsPaymentDisclaimer] = useState<boolean>(!JSON.parse(localStorage.getItem('isPaymentDisclaimerShown') || 'false'));

    const onConfirmPaymentDisclaimer = (): void => {

        localStorage.setItem('isPaymentDisclaimerShown', JSON.stringify(true));
        setIsPaymentDisclaimer(false);
    };

    useEffect(() => {

        requestActivateCardError && setSubmitError(t('general.submitError'));
    }, [requestActivateCardError, t]);

    useEffect(() => {

        reloadGiftCardError && setSubmitError(t('general.submitError'));
    }, [reloadGiftCardError, t]);

    const isLoading = activateGiftCardLoading || reloadGiftCardLoading || createWithPointsLoading;
    const isBusy = !member || !merchant || isLoading;
    const [acknowledged, setAcknowledged] = useState(false);

    const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);

    useEffect(() => {

        if (isBusy) {
            setIsSubmitEnabled(false);
            return;
        }

        if (!isFundWithPoints && !paymentMethod) {
            setIsSubmitEnabled(false);
            return;
        }

        if (amount <= 0) {
            setIsSubmitEnabled(false);
            return;
        }

        if (merchant?.gift_card_restrictions && amount > maxAmount) {
            setIsSubmitEnabled(false);
            return;
        }

        if (!isFundWithPoints && !acknowledged) {
            setIsSubmitEnabled(false);
            return;
        }

        setIsSubmitEnabled(true);
    }
    , [isFundWithPoints, amount, isBusy, merchant?.gift_card_restrictions, paymentMethod, maxAmount, acknowledged]);

    useEffect(() => {

        let newTotal = amount;

        if (!isFundWithPoints) {
            const giftCardFee = member?.customer_gift_card_fee || '0.00';
            const feeAsFloat = parseFloat(giftCardFee.replace('$', ''));

            newTotal = amount + feeAsFloat;
        }

        setTotal(`$${newTotal.toFixed(2)}`);
    }, [amount, isFundWithPoints, member?.customer_gift_card_fee]);

    const schema = yup.object().shape({
        cvv: yup
            .string()
            .required(t('validation.cvv_required'))
            .trim()
            .matches(/^[0-9]{3,4}$/, t('validation.cvv_invalid')),
    });

    const { control, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const { field: cvvField } = useController({
        control,
        defaultValue: '',
        name: 'cvv',
    });

    const onSubmitReload = (): void => {

        // if (cvvField.value.length < 3 || cvvField.value.length > 4) {
        //     setErrorCVV('cvv is required');
        // }
        // else {
        reloadGiftCard({
            member_id: member?.id.toString(),
            card_pin: manageGiftCard.card_pin,
            card_number: manageGiftCard.card_number,
            // cvv: cvvField.value,
            balance: amount.toString(),
            auto_reload: manageGiftCard.auto_reload,
            auto_reload_amount: (belowAmount.toString() || '20.00'),
            purchase_location: '0',
            gift_card_payment_id: paymentMethod?.id || 0,
        }, {
            onError: (_) => {

                setAcknowledged(false);
                setIsConfirmState(false);
            },
            onSuccess: (_) => {

                observer.emit('close-actionsheet');
                setGiftCardValue(amount);
                setIsCompletedModalOpen(true);
                setIsConfirmState(false);
            }
        });
        // }
    };

    const onAddFunds = () => {

        if (!isSubmitEnabled) {
            return;
        }

        if (isFundWithPoints) {
            createWithPoints(
                {
                    member_id: member.id,
                    convert_points: convertPoints,
                    purchase_location: '0'
                },
                {
                    onSuccess: (_) => {

                        observer.emit('close-actionsheet');
                        setGiftCardValue(amount);
                        setIsCompletedModalOpen(true);
                        setIsConfirmState(false);
                    },
                },
            );
        }
        else {
            // no cards yet or there are only closed-loop cards => create a new one
            if (!manageGiftCard || giftCards.every(card => card.is_closed_loop)) {
                requestActivateGiftCard({
                    balance: amount.toString(),
                    auto_reload: !!belowAmount,
                    auto_reload_amount: belowAmount.toString() || '0.00',
                    purchase_location: '0',
                    gift_card_payment_id: paymentMethod?.id || 0,
                    member_id: member?.id.toString(),
                }, {
                    onSuccess: (_) => {

                        observer.emit('close-actionsheet');
                        setGiftCardValue(convertPoints);
                        setIsCompletedModalOpen(true);
                        setIsConfirmState(false);
                    }
                });
                return;
            }

            // else reload the reloadable one
            onSubmitReload();
        }
    };

    if (isConfirmState) {
        return (
            <VStack space={4}>
                <HStack width={'100%'} alignItems={'center'} justifyContent={'space-between'}>
                    <Pressable onPress={() => setIsConfirmState(false)}>
                        <SpIcon icon={faChevronLeft} color={primary}/>
                    </Pressable>
                    <Caption
                        text={'giftCard.addFunds.confirmation.confirm_payment'}
                        fontSize={24}
                        flex={1}
                        textAlign={'center'}
                    />
                </HStack>
                <Caption
                    text={'giftCard.addFunds.confirmation.review_transaction_details'}
                    textAlign={'center'}
                />
                <VStack space={4}>
                    <Box
                        borderWidth={1}
                        borderRadius={radius_sm}
                        borderColor={primary}
                        p={4}
                    >
                        <HStack justifyContent={'space-between'}>
                            <Caption
                                text={'giftCard.addFunds.confirmation.load_amount'}
                            />
                            <Caption>
                                $ { amount.toFixed(2) }
                            </Caption>
                        </HStack>
                        {
                            member?.customer_gift_card_fee &&
                            member?.customer_gift_card_fee !== '$0.00' &&
                            <HStack justifyContent={'space-between'}>
                                <Caption
                                    text={'giftCard.addFunds.confirmation.fee'}
                                />
                                <Caption>
                                    + {member?.customer_gift_card_fee}
                                </Caption>
                            </HStack>
                        }
                        <Divider bg={primary} />
                        <HStack justifyContent="space-between">
                            <Caption
                                text={'giftCard.addFunds.confirmation.total_amount'}
                                fontWeight="bold"
                            />
                            <Caption
                                fontWeight={'bold'}
                                color={highlight_text}
                            >
                                {total}
                            </Caption>
                        </HStack>
                    </Box>
                    <VStack space={4}>
                        <Caption
                            fontSize={'sm'}
                            opacity={.7}
                            textAlign={'center'}
                        >
                            <Caption
                                text={'giftCard.addFunds.confirmation.payment_method'}
                            />
                            <Caption
                                fontWeight={'bold'}
                                color={highlight_text}
                            >
                                xxxx-xxxx-xxxx-{paymentMethod?.card_nickname}
                            </Caption>
                        </Caption>
                        <HStack>
                            <Caption
                                text={'giftCard.addFunds.confirmation.charges'}
                                fontSize={'xs'}
                                opacity={0.7}
                            />
                            <Caption fontSize={'xs'}> &quot;Springbig&quot;</Caption>
                        </HStack>
                    </VStack>
                    <Box bg={'#FFF9C4'} color={'black'} p={'4'} rounded={'lg'} shadow={'2'} w={'100%'} >
                        <VStack space={'4'}>
                            <Caption
                                text={'giftCard.addFunds.confirmation.important_payment_notice'}
                                fontSize={'lg'}
                                fontWeight={'bold'}
                                textAlign={'center'}
                            />
                            <Caption
                                text={'giftCard.addFunds.confirmation.by_using_this_card'}
                                fontSize={'sm'}
                                textAlign={'center'}
                            />
                            <Caption fontSize={'xs'} textAlign={'left'}>
                                {t('giftCard.addFunds.confirmation.all_payments')}
                                {t('giftCard.addFunds.confirmation.fraudulent')}
                            </Caption>
                            <VStack space={'2'} pl={'4'}>
                                <Caption fontSize={'xs'}>
                                    {t('giftCard.addFunds.confirmation.legal_penalties')}
                                </Caption>
                                <Caption fontSize={'xs'}>
                                    {t('giftCard.addFunds.confirmation.account_closure')}
                                </Caption>
                                <Caption fontSize={'xs'}>
                                    {t('giftCard.addFunds.confirmation.credit_damage')}
                                </Caption>
                            </VStack>
                            <HStack space={'3'} alignItems={'center'} justifyContent={'center'}>
                                <CheckBox
                                    isChecked={acknowledged}
                                    onChange={(isChecked) => setAcknowledged(isChecked)}
                                    value={'acknowledge'}
                                />
                                <Caption
                                    text={'giftCard.addFunds.confirmation.i_acknowledge'}
                                    fontSize={'sm'}
                                />
                            </HStack>
                        </VStack>
                    </Box>
                    <Button
                        text={'giftCard.addFunds.confirmation.complete_payment'}
                        onPress={onAddFunds}
                        isDisabled={!isSubmitEnabled}
                    />
                    { !isFundWithPoints &&
                        <Caption
                            text={'giftCard.addFunds.confirmation.please_acknowledge'}
                            textAlign="center"
                        />
                    }
                </VStack>
            </VStack>
        );
    }

    if (isPaymentDisclaimer) {
        return (
            <VStack pt={4} space={4}>
                <VStack p={4} space={4} rounded={'lg'} w={'100%'} bg="#fdf8c1" shadow={2}>
                    <Caption textAlign={'center'} fontSize={'xl'} fontWeight={'bold'}>
                        Important Payment Notice
                    </Caption>
                    <Caption>
                        Dear Valued Member,
                    </Caption>
                    <Caption>
                        To continue using our wallet feature, please update your payment details.
                    </Caption>
                    <Caption>
                        <b>First-time users:</b> Kindly enter your credit/debit card information to start adding funds to your wallet.
                    </Caption>
                    <Caption>
                        <b>Returning users:</b> Due to recent updates to enhance security and functionality, we kindly ask you to re-enter your payment details.
                    </Caption>
                    <Caption>
                        We appreciate your time and understanding as we work to improve your experience.
                    </Caption>
                    <Caption>
                        Thank you for your continued support.
                    </Caption>
                </VStack>
                <Button
                    text={'proceed'}
                    onPress={onConfirmPaymentDisclaimer}
                />
            </VStack>
        );
    }

    return (
        <VStack alignItems={'center'} space={4}>
            {
                !merchant?.hide_gift_card_payments && !isFundWithPoints &&
                <VStack justifyContent={'center'} alignItems={'center'} p={4} bg={'#F48309'}>
                    <Caption color={'white'}>{ merchant?.gift_card_alias } purchases or reloads are temporarily unavailable.</Caption>
                </VStack>
            }
            <Caption fontSize={24}>
                { t('giftCard.addFunds.title') }
            </Caption>
            <VStack width='100%' space={3} flexGrow={1} mt={8} >
                {
                    merchant.points_cash_conversion_enabled &&
                    merchant.create_gift_card_with_points &&
                    (!manageGiftCard || giftCards.every(card => card.is_closed_loop)) &&
                    member.balance >= parseFloat(merchant.dollars_per_point) &&
                    <HStack justifyContent={'space-between'}>
                        <Caption
                            text={'giftCard.addFunds.fundWithPoints'}
                            textValues={[merchant.gift_card_alias]}
                            fontSize={18}
                        />
                        <Switch
                            onTrackColor={primary}
                            offTrackColor={primary?.slice(0, 7) + '40'}
                            _hover={{
                                onTrackColor: primary?.slice(0, 7) + '40',
                                offTrackColor: primary,
                            }}
                            isChecked={isFundWithPoints}
                            onToggle={() => setIsFundWithPoints(!isFundWithPoints)}
                        />
                    </HStack>
                }
                {
                    false && // remove ability to purchase GC altogether
                    !isFundWithPoints &&
                    merchant &&
                    !merchant.hide_gift_card_payments &&
                    <VStack>
                        <Caption
                            text={'general.amount'}
                            fontSize={18}
                        />
                        <AmountDropdown
                            t={t}
                            amount={amount}
                            setAmount={setAmount}
                            amounts={getMenuItems(merchant, giftCardPresetAmounts, maxAmount, minAmount)}
                        />
                    </VStack>
                }
                {
                    false && // remove ability to purchase GC altogether
                    !isFundWithPoints &&
                    merchant &&
                    !merchant.hide_gift_card_payments &&
                    <VStack>
                        <Caption
                            text={'general.payment'}
                            fontSize={18}
                        />
                        <PaymentDropdown
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            t={t}
                        />
                    </VStack>
                }
                {/*{*/}
                {/*    !isFundWithPoints && !(!manageGiftCard || giftCards.every(card => card.is_closed_loop)) &&*/}
                {/*    <FormControl flex={1} isInvalid={!!errorCVV}>*/}
                {/*        <FormControl.Label color={input_label}>*/}
                {/*            { t('credit_card.cvv') }*/}
                {/*        </FormControl.Label>*/}
                {/*        <Input*/}
                {/*            {...cvvField}*/}
                {/*            keyboardType={'numberPad'}*/}
                {/*            backgroundColor={input_background}*/}
                {/*            borderColor={input_border}*/}
                {/*            height={10}*/}
                {/*            fontSize={16}*/}
                {/*            color={input_text}*/}
                {/*            isInvalid={!!errors.cvv}*/}
                {/*            isDisabled={isLoading}*/}
                {/*            maxLength={4}*/}
                {/*            returnKeyType='done'*/}
                {/*        />*/}
                {/*        <FormControl.ErrorMessage*/}
                {/*            leftIcon={<WarningIcon size={3} />}*/}
                {/*        >*/}
                {/*            { errorCVV }*/}
                {/*        </FormControl.ErrorMessage>*/}
                {/*    </FormControl>*/}
                {/*}*/}
                { isFundWithPoints &&
                    <PointsConverterField t={t} onChange={(value) => {

                        if (!merchant?.dollars_per_point) {
                            return;
                        }

                        const newAmount = value / parseFloat(merchant.dollars_per_point);
                        setConvertPoints(value);
                        setAmount(newAmount);
                    }} />
                }
                {
                    !isFundWithPoints &&
                    member?.customer_gift_card_fee &&
                    member?.customer_gift_card_fee !== '$0.00' &&
                    <Caption
                        text={'giftCard.addFunds.conditions.fees'}
                        textValues={[member?.customer_gift_card_fee]}
                        textAlign='center'
                    />
                }
                {
                    (
                        requestActivateCardError || reloadGiftCardError
                    ) &&
                    <CaptionError>
                        {
                            (requestActivateCardError as any)?.data?.message ||
                            (reloadGiftCardError as any)?.data?.message ||
                            (createWithPointsError as any)?.data?.message
                        }
                    </CaptionError>
                }
                <Button
                    text={t('general.add') + ` ${amount}`}
                    fontSize={18}
                    onPress={() => setIsConfirmState(true)}
                    loading={isLoading}
                    disabled={!isSubmitEnabled}
                />
            </VStack>
        </VStack>
    );
};

export default AddFunds;
